// client/src/components/EntityPicker.js
// The one way to fill a field that refers to a person or a firm: type to
// search existing CRM contacts or firms, pick one, or create it on the spot.
//
//   EntitySearch — the search box + dropdown. Used directly by inline
//                  editors (pipeline cells, kanban tiles) and multi-add lists.
//   EntityField  — a form field built on EntitySearch: shows the current
//                  record and switches to search when clicked.
//
// "+ Create" opens a quick-create pop-up (rendered on document.body so it
// works inside table cells and other modals), POSTs the new contact or firm,
// refreshes the cached lists and hands the new record back as the pick, so
// the user never leaves the page.
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { firmNames } from '../lib/contactFirms'
import { useRoleOptions } from '../lib/roles'


const KIND = {
  contact: {
    idKey: 'contact_id', label: 'contact', queryKey: ['contacts'], url: '/api/contacts',
    describe: c => [c.title_role, firmNames(c)].filter(Boolean).join(' · '),
  },
  firm: {
    idKey: 'firm_id', label: 'firm', queryKey: ['firms'], url: '/api/firms',
    describe: f => (Array.isArray(f.firm_type) ? f.firm_type : []).join(', '),
  },
}

const MAX_RESULTS = 50

// All contacts or firms, shared with the rest of the app's cache.
export function useEntityRecords(kind, enabled = true) {
  const k = KIND[kind]
  return useQuery({
    queryKey: k.queryKey,
    queryFn: () => api.get(k.url).then(r => r.data.data ?? []),
    enabled,
  }).data ?? []
}

const inputStyle = {
  background: 'transparent', border: 'none', outline: 'none',
  color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
  width: '100%', padding: 0, minWidth: 80,
}

// ── EntitySearch ──────────────────────────────────────────────────────────
// Props:
//   kind           'contact' | 'firm'
//   records        optional list to search (default: every contact/firm)
//   filter         optional predicate narrowing the default list
//   priorityIds    optional Set shown first, above a divider
//   exclude        optional Set of ids to leave out
//   onPick(id, record)  id null when the clear row is chosen
//   onCancel()     blur / Escape
//   clearLabel     shows a clear row first when given
//   createDefaults defaults for a new record, e.g. { contact_type: ['Investor'] }
//   placeholder, initialQuery
//   autoFocus      false for an always-visible box (results show on focus)
//   multi          stay open and clear the box after each pick (add several)
//   deferCreate    the create row picks a placeholder ("new:<name>") instead
//                  of opening the quick-create pop-up; the caller saves the
//                  record later (PersonForm: new firms, Travis 10/21)
//   inline         results in the page flow instead of a floating dropdown
//                  (for pickers that live inside a pop-up)
export function EntitySearch({
  kind, records, filter, priorityIds = null, exclude = null, onPick, onCancel,
  clearLabel = null, createDefaults = {}, placeholder, initialQuery = '',
  autoFocus = true, multi = false, inline = false, deferCreate = false,
}) {
  const k = KIND[kind]
  const all = useEntityRecords(kind, !records)
  const [query, setQuery]         = useState(initialQuery)
  const [highlight, setHighlight] = useState(0)
  const [creating, setCreating]   = useState(false)
  const [open, setOpen]           = useState(autoFocus)
  const creatingRef = useRef(false)
  const inputRef = useRef(null)

  useEffect(() => { if (autoFocus) inputRef.current?.focus() }, [autoFocus])

  const matches = useMemo(() => {
    let list = records ?? all
    if (filter) list = list.filter(filter)
    if (exclude) list = list.filter(r => !exclude.has(r[k.idKey]))
    const q = query.trim().toLowerCase()
    if (q) list = list.filter(r => (r.name ?? '').toLowerCase().includes(q))
    if (priorityIds) {
      list = [...list.filter(r => priorityIds.has(r[k.idKey])), ...list.filter(r => !priorityIds.has(r[k.idKey]))]
    }
    return list.slice(0, MAX_RESULTS)
  }, [records, all, filter, exclude, query, priorityIds, k.idKey])

  const priorityCount = priorityIds ? matches.filter(r => priorityIds.has(r[k.idKey])).length : 0
  const name = query.trim()

  // Rows: [clear?] + matches + [create?]
  const rows = [
    ...(clearLabel ? [{ type: 'clear' }] : []),
    ...matches.map(r => ({ type: 'record', record: r })),
    ...(name ? [{ type: 'create' }] : []),
  ]

  function pick(id, record) {
    onPick(id, record)
    if (multi) { setQuery(''); setHighlight(0) }
  }

  function choose(i) {
    const row = rows[i]
    if (!row) return
    if (row.type === 'clear') pick(null, null)
    else if (row.type === 'record') pick(row.record[k.idKey], row.record)
    else if (deferCreate) pick(`new:${name}`, { [k.idKey]: `new:${name}`, name, pending: true })
    else { creatingRef.current = true; setCreating(true) }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape')         { e.preventDefault(); onCancel?.() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(rows.length - 1, h + 1)) }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (e.key === 'Enter')     { e.preventDefault(); choose(highlight) }
  }

  const rowStyle = active => ({
    padding: '6px 10px', fontSize: 12, cursor: 'pointer',
    background: active ? 'var(--accent-muted)' : 'transparent',
  })

  return (
    <div style={{ position: 'relative', minWidth: 200 }} onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={query}
        placeholder={placeholder ?? `Type to search ${k.label}s…`}
        style={inputStyle}
        onChange={e => { setQuery(e.target.value); setHighlight(0); setOpen(true) }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        // Blur closes (and cancels), after any row mousedown has fired. Not
        // while the create pop-up is open: it takes focus by design.
        onBlur={() => setTimeout(() => {
          if (creatingRef.current) return
          setOpen(false)
          onCancel?.()
        }, 150)}
      />
      {open && !creating && (
        <div style={{
          ...(inline
            ? { marginTop: 8 }
            : { position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }),
          minWidth: 260, maxHeight: 8 * 34, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 6,
        }}>
          {rows.length === 0 && (
            <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
              Start typing a name
            </div>
          )}
          {rows.map((row, i) => {
            const common = {
              key: row.type === 'record' ? row.record[k.idKey] : row.type,
              onMouseEnter: () => setHighlight(i),
              onMouseDown: e => { e.preventDefault(); choose(i) },
            }
            if (row.type === 'clear') {
              return <div {...common} style={{ ...rowStyle(highlight === i), color: 'var(--text-muted)', fontStyle: 'italic' }}>{clearLabel}</div>
            }
            if (row.type === 'create') {
              return (
                <div {...common} style={{ ...rowStyle(highlight === i), color: 'var(--accent)', borderTop: matches.length ? '1px solid var(--border)' : undefined }}>
                  {deferCreate
                    ? <>+ New {k.label[0].toUpperCase() + k.label.slice(1)} “{name}” <span style={{ color: 'var(--text-muted)' }}>· details after saving</span></>
                    : <>+ Create {k.label} “{name}”</>}
                </div>
              )
            }
            const r = row.record
            const idx = i - (clearLabel ? 1 : 0)
            const sub = k.describe(r)
            return (
              <div {...common} style={{ ...rowStyle(highlight === i), borderTop: priorityCount > 0 && idx === priorityCount ? '1px solid var(--border)' : undefined }}>
                <span style={{ color: 'var(--text-primary)' }}>{r.name}</span>
                {sub && <span style={{ color: 'var(--text-muted)' }}> · {sub}</span>}
              </div>
            )
          })}
        </div>
      )}
      {creating && (
        <QuickCreateModal
          kind={kind}
          initialName={name}
          defaults={createDefaults}
          onCancel={() => { creatingRef.current = false; setCreating(false); inputRef.current?.focus() }}
          onCreated={record => {
            creatingRef.current = false; setCreating(false); pick(record[k.idKey], record)
            if (multi) inputRef.current?.focus()
          }}
        />
      )}
    </div>
  )
}

// ── EntityField ───────────────────────────────────────────────────────────
// A form field: shows the chosen record, click to search/replace/create.
// Props: kind, value (id or ''/null), onChange(id|null, record), plus any
// EntitySearch props (records, filter, createDefaults, …). allowClear adds a
// "None" row. disabled shows the value read-only.
export function EntityField({ kind, value, onChange, allowClear = true, disabled = false, emptyText = 'None', ...searchProps }) {
  const k = KIND[kind]
  const all = useEntityRecords(kind, !searchProps.records)
  const [editing, setEditing] = useState(false)
  const [picked, setPicked]   = useState(null)   // record just created/picked, before lists refresh
  const list = searchProps.records ?? all
  const current = value ? (list.find(r => r[k.idKey] === value) ?? (picked?.[k.idKey] === value ? picked : null)) : null

  const box = {
    display: 'flex', alignItems: 'center', minHeight: 36, padding: '6px 10px',
    background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 6,
    cursor: disabled ? 'default' : 'text', fontSize: 13,
  }

  if (editing && !disabled) {
    return (
      <div style={box}>
        <EntitySearch
          kind={kind}
          clearLabel={allowClear ? emptyText : null}
          {...searchProps}
          onPick={(id, record) => { setPicked(record); onChange(id, record); setEditing(false) }}
          onCancel={() => setEditing(false)}
        />
      </div>
    )
  }

  return (
    <div style={box} onClick={() => !disabled && setEditing(true)} tabIndex={disabled ? -1 : 0}
      onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setEditing(true) } }}>
      {current
        ? <span style={{ color: 'var(--text-primary)' }}>
            {current.name}
            {current.pending && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · new {k.label}, details after saving</span>}
          </span>
        : <span style={{ color: 'var(--text-muted)' }}>
            {value ? 'Loading…' : (searchProps.placeholder ?? `Type to search ${k.label}s…`)}
          </span>}
    </div>
  )
}

// ── Quick-create pop-up ───────────────────────────────────────────────────
function QuickCreateModal({ kind, initialName, defaults, onCancel, onCreated }) {
  const { contactTypes, firmTypes } = useRoleOptions()
  const qc = useQueryClient()
  const [form, setForm] = useState(() => kind === 'contact'
    ? { name: initialName, email: defaults.email ?? '', title_role: defaults.title_role ?? '', contact_type: defaults.contact_type ?? [], primary_firm_id: defaults.primary_firm_id ?? null }
    : { name: initialName, website: '', firm_type: defaults.firm_type ?? [] })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)

  const set = (key, v) => { setForm(f => ({ ...f, [key]: v })); setError(null) }
  const toggle = (key, t) => set(key, form[key].includes(t) ? form[key].filter(x => x !== t) : [...form[key], t])

  async function save(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!form.name.trim()) return setError('Name is required')
    if (kind === 'firm' && !form.firm_type.length) return setError('Pick at least one firm type')
    const payload = kind === 'contact'
      ? {
          name: form.name.trim(),
          ...(form.email.trim() && { email: form.email.split(',').map(s => s.trim()).filter(Boolean) }),
          ...(form.title_role.trim() && { title_role: form.title_role.trim() }),
          ...(form.contact_type.length && { contact_type: form.contact_type }),
          ...(form.primary_firm_id && { primary_firm_id: form.primary_firm_id }),
        }
      : {
          name: form.name.trim(),
          firm_type: form.firm_type,
          ...(form.website.trim() && { website: form.website.trim() }),
        }
    setSaving(true)
    try {
      const { data } = await api.post(KIND[kind].url, payload)
      const record = data.data
      qc.invalidateQueries({ queryKey: KIND[kind].queryKey })
      if (kind === 'contact') qc.invalidateQueries({ queryKey: ['partners'] })
      onCreated(record)
    } catch (err) {
      setError(err.response?.data?.error || err.message)
      setSaving(false)
    }
  }

  const label = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }
  const types = kind === 'contact' ? contactTypes : firmTypes
  const typeKey = kind === 'contact' ? 'contact_type' : 'firm_type'

  return createPortal(
    // React bubbles portal events to the picker's ancestors (forms, table
    // cells, rows); keep them inside the pop-up.
    <div onMouseDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onCancel() }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <form onSubmit={save} style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
        width: '100%', maxWidth: 460, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>New {KIND[kind].label}</h2>
          <button type="button" className="btn-icon" onClick={onCancel} style={{ fontSize: 16 }}>✕</button>
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

        <div>
          <label style={label}>Name *</label>
          <input className="input" autoFocus value={form.name} onChange={e => set('name', e.target.value)} />
        </div>

        {kind === 'contact' ? (
          <>
            <div>
              <label style={label}>Email(s)</label>
              <input className="input" value={form.email} placeholder="Comma-separated" onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label style={label}>Title / Role</label>
              <input className="input" value={form.title_role} onChange={e => set('title_role', e.target.value)} />
            </div>
            <div>
              <label style={label}>Firm</label>
              <EntityField kind="firm" value={form.primary_firm_id} onChange={id => set('primary_firm_id', id)}
                createDefaults={{ firm_type: [] }} />
            </div>
          </>
        ) : (
          <div>
            <label style={label}>Website</label>
            <input className="input" value={form.website} placeholder="example.com" onChange={e => set('website', e.target.value)} />
          </div>
        )}

        <div>
          <label style={label}>{kind === 'contact' ? 'Contact type' : 'Firm type *'}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {types.map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={form[typeKey].includes(t)} onChange={() => toggle(typeKey, t)}
                  style={{ accentColor: 'var(--accent)' }} />
                {t}
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : `Create ${KIND[kind].label}`}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

// ── EntityMultiField ──────────────────────────────────────────────────────
// Several contacts or firms (e.g. an interaction's participants): chosen
// records as removable chips, plus a search box that adds more or creates
// one. value is an array of ids; onChange gets the new array.
export function EntityMultiField({ kind, value, onChange, records, placeholder, createDefaults }) {
  const k = KIND[kind]
  const all = useEntityRecords(kind, !records)
  const [created, setCreated] = useState({})   // created here, before the cached list refreshes
  const list = records ?? all
  const nameOf = id => (list.find(r => r[k.idKey] === id) ?? created[id])?.name ?? '…'

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {value.map(id => (
            <span key={id} className="badge badge-blue" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {nameOf(id)}
              <button type="button" title="Remove" onClick={() => onChange(value.filter(v => v !== id))}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ padding: '8px 10px', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <EntitySearch kind={kind} records={records} autoFocus={false} multi
          exclude={new Set(value)} createDefaults={createDefaults}
          placeholder={placeholder ?? `Type to add a ${k.label}…`}
          onPick={(id, record) => {
            if (!id) return
            if (record) setCreated(c => ({ ...c, [id]: record }))
            onChange([...value, id])
          }} />
      </div>
    </div>
  )
}
